import type {
  MessageFetchInFlight,
  useConversationMessageStore,
} from '@/core/conversations/message/conversation-message.store';

type MessageStore = ReturnType<typeof useConversationMessageStore>;

export function cancelMessageFetches(
  store: MessageStore,
  shouldCancel: (fetch: MessageFetchInFlight) => boolean,
): void {
  const inFlight = store.messageFetchesInFlight;
  inFlight.filter(shouldCancel).forEach((fetch) => fetch.controller.abort());
  store.messageFetchesInFlight = inFlight.filter((fetch) => !shouldCancel(fetch));
}

// Aborting the in-flight attempt for a repeat (a double scroll event) would lose that page;
// only a conversation load (clearMessages) takes a page over.
export function isRepeatOfPageInFlight(
  store: MessageStore,
  requestId: number,
  { page, clearMessages }: { page: number; clearMessages?: boolean },
): boolean {
  return (
    !clearMessages &&
    store.messageFetchesInFlight.some(
      (fetch) => fetch.requestId === requestId && fetch.page === page,
    )
  );
}

// Other pages of the same load (the 5s poll and a history page) run side by side and
// both commit; a fetch that reaches here for a page in flight takes that page over.
function startMessageFetch(store: MessageStore, requestId: number, page: number): AbortController {
  cancelMessageFetches(store, (fetch) => fetch.requestId !== requestId || fetch.page === page);
  const controller = new AbortController();
  store.messageFetchesInFlight.push({ requestId, page, controller });
  return controller;
}

function finishMessageFetch(store: MessageStore, controller: AbortController): void {
  store.messageFetchesInFlight = store.messageFetchesInFlight.filter(
    (fetch) => fetch.controller !== controller,
  );
}

export async function trackMessageFetch<T>(
  store: MessageStore,
  requestId: number,
  page: number,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = startMessageFetch(store, requestId, page);
  try {
    return await run(controller.signal);
  } finally {
    finishMessageFetch(store, controller);
  }
}
